import { getGtmContentGroup } from '~/utils/getGtmContentGroup'
import { useIsSubdomain } from '~/composables/useSubdomain'

export default defineNuxtRouteMiddleware((to, from) => {
  if (import.meta.server) return

  const {
    public: { baseUrl },
  } = useRuntimeConfig()
  const gtm = useKftGtm()
  const excludedRoutes = [
    '/embedded/',
    '/ping/',
    '/widgets/',
    // KFT-3806: transient redirector pages that return from an external
    // referrer (Stripe / Google & Facebook OAuth) and immediately hard-redirect
    // onward. Booting GA4 here mints a phantom "Direct" session before the real
    // destination page resumes the session via the persistent _ga cookie.
    '/payment/result/',
    '/auth/google/callback/',
    '/auth/facebook/callback/',
  ]

  const { isEmbedded } = useEmbeddedCheckout()
  const isSubdomain = useIsSubdomain()

  const shouldEnableGtm =
    gtm &&
    gtm.enable &&
    !isEmbedded.value &&
    !excludedRoutes.some((x) => to.path.match(x)) &&
    !isSubdomain.value

  if (!shouldEnableGtm) return

  // KFT-3883: defer the gtm.js injection off the hydration critical path.
  // Enabling GTM synchronously here runs gtm.js (and every downstream tag —
  // GA4, Hotjar, ad pixels) during the busiest main-thread window, inflating
  // TBT. Scheduling enable() on idle moves that work after hydration while
  // still loading on every page, so no pageview is lost.
  //
  // The `timeout` MUST stay below the 2000ms pageview push below: that push is
  // gated by `gtm.enabled()` in useKftGtm.pushToDataLayer, so enable() has to
  // run first or the nuxtRoute PageView is dropped.
  const scheduleIdle: (cb: () => void) => void =
    typeof window.requestIdleCallback === 'function'
      ? (cb) => window.requestIdleCallback(cb, { timeout: 1000 })
      : (cb) => {
          setTimeout(cb, 1)
        }

  scheduleIdle(() => gtm.enable())

  // Skip if staying on the same page (not initial load)
  if (from.name && to.path === from.path) return

  // Skip if this is initial load and route is not fully resolved yet
  // (routeName will be undefined if i18n hasn't processed the route)
  if (!from.name && !to.name) return

  setTimeout(() => {
    const pageTitle =
      typeof document !== 'undefined' ? document?.title || '' : ''
    gtm.pushToDataLayer({
      routeName: to.name,
      content_group: getGtmContentGroup(to),
      pageType: 'PageView',
      pageUrl: baseUrl + to.fullPath,
      pagePath: to.fullPath,
      pageTitle,
      event: 'nuxtRoute',
    })
  }, 2000)
})
